import { Expose, Exclude, Transform } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate } from './Models'
import { parseLocalizedText, type LocalizedText, type RevisionSource } from './registry'

/** Where a regulation_lists row came from. Manual rows carry the literal string 'manual';
 *  extraction-worker rows carry a source descriptor object. */
export interface RegulationListItemSource {
  document_id?: string
  import_batch?: string
}

/**
 * Resolves a row's `as_typed`/`designation` free text to a canonical catalogue entry.
 * Written ONLY by the backend resolver — never write this field from the front.
 */
export interface RegulationRegistryRef {
  family_id: string
  /** MYAC-904 v2: the backend resolver stamps `revision_key` (renamed from the
   *  pre-rename `edition_key`) — see regulatory-monitoring resolver `_stamp_row`.
   *  Null/absent is first-class (spec §8 row 7): the resolver couldn't pin an
   *  exact revision, defaulting to the family's `latest_revision`. */
  revision_key: string | null
  /** MYAC-904 P4.3 D1/D4 — provenance of the pin above (`typed` / `default` /
   *  `user`). Server-written only; distinguishes an honest resolver snapshot
   *  (`default`) from a confirmed pick, which is what lets the front offer a
   *  "confirm edition" affordance instead of silently trusting a latest-pin.
   *  Note: this field has no `@Transform` parser (see `registry_ref`'s plain
   *  pass-through below) — it is typed here for compile-time safety only. */
  revision_source?: RevisionSource
}

/** Lane-1 "did you mean X?" candidate on the ROW (spec §7.1) — offered when
 *  the resolver can neither match nor confidently mint an identity for the
 *  typed text, naming only EXISTING families. Distinct from a family's own
 *  `unverifiable_suggestions` (curator-side near-miss list on an already
 *  resolved-but-unverified family) — see `regulationResolutionState.ts`. */
export interface RegulationResolutionSuggestion {
  family_id: string | null
  revision_key: string | null
  /** LocalizedText per spec §7.1 ("why? = LocalizedText, optional"). */
  why: LocalizedText | null
}

function parseResolutionSuggestion(raw: unknown): RegulationResolutionSuggestion | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  return {
    family_id: typeof r.family_id === 'string' && r.family_id ? r.family_id : null,
    revision_key: typeof r.revision_key === 'string' && r.revision_key ? r.revision_key : null,
    why: r.why ? parseLocalizedText(r.why) : null,
  }
}

function parseResolutionSuggestions(raw: unknown): RegulationResolutionSuggestion[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null
  const parsed = raw.map(parseResolutionSuggestion).filter((s): s is RegulationResolutionSuggestion => s !== null)
  return parsed.length > 0 ? parsed : null
}

/** Row legibility marker (spec §7.1) — NOT a tier/quality stamp. `'resolving'`
 *  = the resolver hasn't stamped a `registry_ref` yet; `'unrecognized'` = the
 *  typed text could not be given ANY identity (server-only quarantine entry
 *  in `registry_unresolved`); `null` = a ref already exists, or this is a
 *  legacy row predating the field. */
// 'escalated' (MYAC-1002, 2026-08-13): the resolve lane parked this row's
// designation for a human — renders as the "awaiting review" chip, never
// the resolving spinner. Review finding on PR #432: this allowlist silently
// downgraded the value to null, killing the chip before it ever fired.
export type RegulationRegistryState = 'resolving' | 'unrecognized' | 'escalated' | null
const REGISTRY_STATES: ReadonlySet<string> = new Set(['resolving', 'unrecognized', 'escalated'])
function toRegistryState(v: unknown): RegulationRegistryState {
  return typeof v === 'string' && REGISTRY_STATES.has(v) ? (v as RegulationRegistryState) : null
}

@Exclude()
export class RegulationListItem extends _FirebaseDocument {
  @Expose()
  public designation: string = ''

  @Expose()
  public title: string = ''

  @Expose()
  public normative_body: string = ''

  @Expose()
  @Transform(anyToDate)
  public last_reviewed_at: Date | null = null

  /** TCK-J — stamped by the regulatory-monitoring watch run on every row it
   *  actually examined (including `unchanged` rows), with the report's
   *  `generated_at`. Written ONLY by the backend job via the Admin SDK —
   *  never write this field from the front, and it is deliberately absent
   *  from the client write allowlist in `firestore.rules`, the same treatment
   *  `registry_ref` gets. Distinct from `last_reviewed_at`: this date says
   *  "the watch passed over this row", never "a human judged it". */
  @Expose()
  @Transform(anyToDate)
  public last_checked_at: Date | null = null

  /** Designation exactly as the user typed it (or the extraction worker's raw text), before trimming/normalization. */
  @Expose()
  public as_typed: string | null = null

  /** Provenance of the row: 'manual' for front-created rows, or a descriptor for imported rows. */
  @Expose()
  public source: RegulationListItemSource | string | null = null

  /** READ-only from the front — set exclusively by the backend resolver. Never write from front code. */
  @Expose()
  public registry_ref: RegulationRegistryRef | null = null

  /** The edition the user asked for (written by the front's edition gestures;
   *  the backend then re-pins `registry_ref`). Read to know a change is made
   *  before the backend has caught up (MYAC-1496 lot 1, 02/10). */
  @Expose()
  public resolver_hint: { family_id: string; revision_key: string | null } | null = null

  /** @deprecated MYAC-904 P4.2 (expand -> migrate -> contract, decision
   *  2026-07-29): the stored resolution stamp goes stale the moment a family
   *  is curated after stamp time and is no longer read by the front — the
   *  chip is derived LIVE from `registry_families` instead (see
   *  `regulationResolutionState.ts`). Field kept only because the resolver
   *  still writes it (it stops once this ships, per the plan's expand ->
   *  contract note); never consume it in new code. */
  @Expose()
  public registry_resolution: 'public' | 'candidate' | null = null

  /** READ-only from the front — set exclusively by the backend resolver.
   *  Legibility-only marker (spec §7.1), NOT a tier/quality stamp. */
  @Expose()
  @Transform(({ obj, key }) => toRegistryState(obj[key]))
  public registry_state: RegulationRegistryState = null

  /** READ-only from the front — Lane-1 did-you-mean candidates (spec §7.1),
   *  present only alongside `registry_state === 'unrecognized'`. */
  @Expose()
  @Transform(({ obj, key }) => parseResolutionSuggestions(obj[key]))
  public resolution_suggestions: RegulationResolutionSuggestion[] | null = null

  /** MYAC-1607 — the client's own categories, `{category: [values]}`, written
   *  by the front's category editor with `tags_flat` (`['category:<value>']`),
   *  always both together. Absent on a row nobody categorised; read it
   *  through `categoriesOf` (`utils/regulationCategories.ts`). */
  @Expose()
  public tags: Record<string, string[]> | null = null

  @Expose()
  public tags_flat: string[] | null = null

  collection(): string {
    return 'regulation_lists'
  }

  static fromFirebase(o: DocumentSnapshot): RegulationListItem | null {
    return _FirebaseDocument._fromFirebase(RegulationListItem, o)
  }
}
